(ns ui.animation-editor.model
  "The pure part of the Replicant story template (animation) editor: the
   entity the editor starts with, the layers with their tabs, the edits
   of the entity and the stage model that `ui.animation-editor.stage`
   applies to the CreateJS composition. Everything in here runs on the
   JVM as well, see `ui.animation-editor.model-test`."
  (:require [ui.animation-editor.templates :as templates]))

(def layers
  "The four layers of a story template, in the order of the tabs."
  [{:layer :background
    :tab "Background"}
   {:layer :headline
    :tab "Headline"
    :label "Headline text"}
   {:layer :teaser
    :tab "Teaser text"
    :label "Teaser text"}
   {:layer :swipe
    :tab "Swipe up"
    :label "Swipe text"}])

(def tabs
  (mapv :tab layers))

(defn layer-of-tab
  [tab]
  (some (fn [layer]
          (when (= tab (:tab layer))
            layer))
        layers))

(defn attr
  "The attribute `attr-name` of the `layer`, for example
   `:instagram.animation.headline/font`."
  [layer attr-name]
  (keyword (str "instagram.animation." (name layer))
           attr-name))

(defn text-attr
  [layer]
  (attr layer "text"))

(defn font-attr
  [layer]
  (attr layer "font"))

(defn symbol-attr
  [layer]
  (attr layer "symbol-name"))

(defn symbol-name
  "The symbol of the composition that animates the `layer`."
  [entity layer]
  (get entity (symbol-attr layer)))

(defn default-symbol-name
  [layer]
  (:symbol-name (first (templates/available layer))))

(defn default-font
  [size]
  {:type :type/font
   :font/color "#ffffff"
   :font/alpha 1.0
   :font/family "Arial"
   :font/size size
   :font/outline 0
   :font/line-height 0
   :font/shadow false})

(def default-entity
  "What a new story template looks like before the user edits it."
  {:type :type.instagram/animation
   :instagram.animation.headline/text "Headline"
   :instagram.animation.headline/font (default-font 75)
   :instagram.animation.headline/symbol-name (default-symbol-name :headline)
   :instagram.animation.teaser/text "This is a teaser text to tease you!"
   :instagram.animation.teaser/font (default-font 50)
   :instagram.animation.teaser/symbol-name (default-symbol-name :teaser)
   :instagram.animation.swipe/text "Swipe up"
   :instagram.animation.swipe/font (default-font 50)
   :instagram.animation.swipe/symbol-name (default-symbol-name :swipe)
   :instagram.animation.background/color "#000000"
   :instagram.animation.background/symbol-name (default-symbol-name :background)})

(defn initial-entity
  "The entity the editor starts with: the defaults, overridden by what
   the `:query/animation-editor` returned (the copy of the edited story
   template, or only the background image of a new one)."
  [loaded]
  (merge default-entity
         loaded))

(defn set-text
  [entity layer text]
  (assoc entity (text-attr layer) text))

(defn set-font
  [entity layer font-attr-key value]
  (assoc-in entity [(font-attr layer) font-attr-key] value))

(defn set-symbol
  [entity layer symbol-name]
  (assoc entity (symbol-attr layer) symbol-name))

(defn set-background-color
  [entity color]
  (assoc entity :instagram.animation.background/color color))

(def font-sliders
  "The sliders of the text settings. The alpha is edited in percent."
  [{:attr :font/size
    :label "Size"
    :min 20
    :max 300
    :step 5}
   {:attr :font/alpha
    :label "Alpha"
    :title "Low alpha equals transparent text. High alpha leads to opaque text"
    :min 0
    :max 100
    :step 1}
   {:attr :font/outline
    :label "Outline"
    :title "Outlines the font. 0 means no outline."
    :min 0
    :max 10
    :step 1}
   {:attr :font/line-height
    :label "Line height"
    :title "The distance between two lines of text. 0 is the standard line height."
    :min 0
    :max 500
    :step 5}])

(defn slider-value
  "The position of the slider for the font attribute `attr-key`."
  [font attr-key]
  (if (= :font/alpha attr-key)
    (Math/round (* 100 (double (:font/alpha font 1.0))))
    (get font attr-key 0)))

(defn parse-slider-value
  "The value of the font attribute `attr-key` for the slider position
   `s` (a string, as the DOM reports it). Nil for an unreadable string."
  [attr-key s]
  (when-let [n (parse-double (str s))]
    (if (= :font/alpha attr-key)
      (/ n 100.0)
      (Math/round n))))

(defn background-image
  [entity]
  (:instagram.animation.background/image entity))

(defn image-url
  "The URL that loads the image of the blob. `/image/` redirects to the
   blob storage, which allows the cross-origin use on the canvas."
  [blob-id]
  (str "/image/" blob-id))

(defn background-image-url
  [entity]
  (some-> (:blob/id (background-image entity))
          (image-url)))

(defn savable?
  "A story template needs a background image, which the editor cannot
   choose: it comes from the story image the template is created for."
  [entity]
  (boolean (:blob/id (background-image entity))))

(def default-shadow
  {:color "#000000"
   :offsetX 3
   :offsetY 3
   :blur 4})

(defn font-properties
  "The properties of the CreateJS text of a layer for the `font`."
  [font]
  {:color (:font/color font)
   :alpha (:font/alpha font)
   :font (str (:font/size font) "px '" (:font/family font) "'")
   :outline (:font/outline font)
   :lineHeight (:font/line-height font)
   :shadow (when (:font/shadow font)
             default-shadow)})

(defn text-model
  [entity layer]
  {:text (assoc (font-properties (get entity (font-attr layer)))
                :text (get entity (text-attr layer)))
   :visible true})

(defn background-model
  [entity]
  (cond-> {:visible true}
    (background-image-url entity)
    (assoc :instance {:image {:crossOrigin "anonymous"
                              :src (background-image-url entity)}})))

(defn hidden-model
  []
  (into {}
        (map (fn [symbol-name]
               [symbol-name {:visible false}]))
        templates/symbol-names))

(defn stage-model
  "What `ui.animation-editor.stage` merges into the `exportRoot` of the
   composition: every symbol hidden, except the chosen one per layer with
   its text and font, and the chosen background with its image. Same
   model as `storrito.instagram.story.renderer.core/stage-model`, which
   the recorder page uses to record the video."
  [entity]
  (merge (hidden-model)
         {(symbol-name entity :headline) (text-model entity :headline)
          (symbol-name entity :teaser) (text-model entity :teaser)
          (symbol-name entity :swipe) (text-model entity :swipe)
          (symbol-name entity :background) (background-model entity)}))

(defn parse-eid
  [s]
  (when (and (string? s)
             (re-matches #"\d+" s))
    (parse-long s)))

(defn query-of-location
  "The `:query/animation-editor` for the query params of the page URL:
   `?eid=` edits the story template with that eid (the editor gets a
   copy of it), `?image-eid=` creates a story template for that story
   image. Nil when neither is given: the editor then starts with the
   defaults and cannot be saved, since it has no background image."
  [query-params]
  (let [eid (parse-eid (:eid query-params))
        image-eid (parse-eid (:image-eid query-params))]
    (cond
      eid {:query/kind :query/animation-editor
           :query/data {:animation-editor/eid eid}}
      image-eid {:query/kind :query/animation-editor
                 :query/data {:animation-editor/image-eid image-eid}})))

(def editor-url
  "The editor without a story template or image: it cannot be saved."
  "/ui/animation-editor")

(defn edit-url
  "The URL of the editor for the story template `eid`."
  [eid]
  (str editor-url "?eid=" eid))

(defn new-url
  "The URL of the editor for a new story template on the story image
   `image-eid`."
  [image-eid]
  (str editor-url "?image-eid=" image-eid))
